import React from "react";
import { Radio, Checkbox, Avatar } from "antd";
import CustomTypography from "./CustomTypography";

/**
 * Custom List Item Component
 *
 * @param {Object} item - The item data to display
 * @param {boolean} selected - Whether the item is selected
 * @param {function} onSelect - Callback when item is selected
 * @param {string} selectionType - Type of selection: 'radio', 'checkbox', or 'none'
 * @param {ReactNode} customAvatar - Custom avatar element (optional)
 * @param {ReactNode} customTitle - Custom title element (optional)
 * @param {ReactNode} customSubtitle - Custom subtitle element (optional)
 * @param {ReactNode} customExtra - Custom extra content (optional)
 */
const CustomListItem = ({
  item,
  selected = false,
  onSelect,
  selectionType = "none", // 'radio', 'checkbox', 'none'
  customAvatar,
  customTitle,
  customSubtitle,
  customExtra,
  avatarSize = 40,
  className = "",
}) => {
  // Get initials from name
  const getInitials = () => {
    const name = item.name || item.fullName || item.title || "";
    const words = name.split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleClick = () => {
    if (onSelect && selectionType !== "none") {
      onSelect(item.id || item.key);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 ${
        selected
          ? "bg-[#E6F4FF] border border-solid border-[#91CAFF]"
          : "bg-white hover:bg-[#F5F5F5]"
      } ${selectionType !== "none" ? "cursor-pointer" : ""} rounded w-full ${className}`}
      onClick={handleClick}
    >
      {/* Selection Control (Radio/Checkbox) */}
      {selectionType === "radio" && <Radio checked={selected} />}
      {selectionType === "checkbox" && <Checkbox checked={selected} />}

      {/* Avatar */}
      {customAvatar || (
        <Avatar size={avatarSize} src={item.avatar || item.profilePic || item.image}>
          {!item.avatar && !item.profilePic && !item.image && getInitials()}
        </Avatar>
      )}

      {/* Content */}
      <div className="flex-1">
        {/* Title */}
        <div className="mb-1">
          {customTitle || (
            <div className="flex items-center gap-1">
              <CustomTypography variant="text" strong className="!text-black">
                {item.name || item.fullName || item.title}
              </CustomTypography>
              {item.companyRole?.name && (
                <CustomTypography variant="text" className="text-[#000000A6]">
                  ({item.companyRole?.name})
                </CustomTypography>
              )}
            </div>
          )}
        </div>

        {/* Subtitle */}
        {customSubtitle ||
          ((item.subtitle || item.description || item.phone) && (
            <CustomTypography variant="text" className="text-[#000000A6]">
              {item.subtitle || item.description || item.phone}
            </CustomTypography>
          ))}
      </div>

      {/* Extra Content */}
      {customExtra}
    </div>
  );
};

export default CustomListItem;
